import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Loader2, Eye, EyeOff, MailCheck, Sparkles, Check } from "lucide-react";

export default function AuthPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login, signup } = useAuth();
  const [mode, setMode] = useState(() => {
    const m = params.get("mode");
    if (m === "signup") return "signup";
    if (m === "signin") return "signin";
    if (m === "forgot") return "forgot";
    // Returning users (installed PWA / prior login) default to Sign in
    try { if (localStorage.getItem("sp_returning")) return "signin"; } catch { /* ignore */ }
    return "signup";
  });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("sp_kicked")) {
      localStorage.removeItem("sp_kicked");
      toast("Signed out", { description: "Your account was opened on another device. Sign in again to continue here." });
    }
  }, []);

  const finishLogin = (u) => navigate("/dashboard");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email });
        setSent(true);
      } else if (mode === "signup") {
        await signup(email, password, fullName);
        toast.success("You're in — your 15-day free trial has started 🎉");
        navigate("/onboarding");
      } else {
        const u = await login(email, password);
        finishLogin(u);
      }
    } catch (err) {
      if (err?.response?.status === 409 && err?.response?.data?.detail === "device_limit") {
        toast("This account is already active on 2 devices", {
          description: "Continue here and sign out the oldest device?",
          duration: 12000,
          action: {
            label: "Continue here",
            onClick: async () => {
              try { const u = await login(email, password, true); finishLogin(u); }
              catch { toast.error("Couldn't sign in on this device"); }
            },
          },
        });
      } else {
        toast.error(err?.response?.data?.detail || "Something went wrong");
      }
    } finally {
      setBusy(false);
    }
  };

  const title = mode === "signup" ? "Start your free trial" : mode === "forgot" ? "Reset your password" : "Welcome back";
  const subtitle = mode === "signup"
    ? "15 days free to create & send punch lists. No credit card required."
    : mode === "forgot"
    ? "Enter your email and we'll send you a reset link."
    : "Sign in to your dashboard.";

  return (
    <div className="flex min-h-[100dvh] flex-col bg-background">
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col justify-center px-6 py-10">
        <button onClick={() => navigate("/")} className="mb-8 flex items-center gap-2" data-testid="auth-logo">
          <img src="/logo-mark.webp" alt="Send-Proof" className="h-9 w-9 rounded-lg" />
          <span className="font-heading text-xl font-bold tracking-tight">Send-Proof</span>
        </button>

        <h1 className="font-heading text-2xl font-bold">{title}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>

        {mode === "signup" && (
          <div className="mt-5 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4" data-testid="trial-banner">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-emerald-500" />
              <span className="text-sm font-bold text-emerald-500">15-day free trial · no credit card</span>
            </div>
            <ul className="mt-2.5 space-y-1.5">
              {["Create & send unlimited punch lists", "Cancel anytime — we'll remind you before it ends", "Just $9.99/mo after, only if you love it"].map((t) => (
                <li key={t} className="flex items-start gap-2 text-[13px] text-foreground">
                  <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-500" strokeWidth={3} />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {mode === "forgot" && sent ? (
          <div className="mt-6 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-5 text-center" data-testid="forgot-sent">
            <MailCheck className="mx-auto h-9 w-9 text-emerald-500" />
            <p className="mt-3 text-sm font-semibold text-foreground">Check your email</p>
            <p className="mt-1 text-sm text-muted-foreground">
              If an account exists for <span className="font-medium text-foreground">{email}</span>, a reset link is on its way. It expires in 1 hour.
            </p>
            <button
              data-testid="forgot-back-to-signin"
              onClick={() => { setMode("signin"); setSent(false); }}
              className="mt-4 text-sm font-semibold text-emerald-600"
            >
              Back to sign in
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            {mode === "signup" && (
              <div>
                <Label htmlFor="name">Full name</Label>
                <Input id="name" data-testid="auth-name-input" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Jane Cooper" className="mt-1 h-11" />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required data-testid="auth-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="mt-1 h-11" />
            </div>
            {mode !== "forgot" && (
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  {mode === "signin" && (
                    <button
                      type="button"
                      data-testid="forgot-password-link"
                      onClick={() => { setMode("forgot"); setSent(false); }}
                      className="text-xs font-semibold text-emerald-600"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPw ? "text" : "password"}
                    required
                    data-testid="auth-password-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="h-11 pr-11"
                  />
                  <button
                    type="button"
                    data-testid="auth-password-toggle"
                    aria-label={showPw ? "Hide password" : "Show password"}
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute right-0 top-0 flex h-11 w-11 items-center justify-center text-muted-foreground transition-transform active:scale-90 hover:text-foreground"
                  >
                    {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
            )}
            <Button type="submit" disabled={busy} data-testid="auth-submit-btn" className="h-11 w-full bg-emerald-500 text-base hover:bg-emerald-600">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : mode === "signup" ? "Start free trial" : mode === "forgot" ? "Send reset link" : "Sign in"}
            </Button>
            {mode === "signup" && (
              <p className="text-center text-[11px] text-muted-foreground">No charge today · Viewing lists shared with you is always free</p>
            )}
          </form>
        )}

        <p className="mt-6 text-center text-sm text-muted-foreground">
          {mode === "forgot" ? (
            <>Remembered it?{" "}
              <button data-testid="auth-toggle-mode" className="font-semibold text-emerald-600" onClick={() => { setMode("signin"); setSent(false); }}>
                Sign in
              </button>
            </>
          ) : (
            <>{mode === "signup" ? "Already have an account?" : "New to Send-Proof?"}{" "}
              <button
                data-testid="auth-toggle-mode"
                className="font-semibold text-emerald-600"
                onClick={() => setMode(mode === "signup" ? "signin" : "signup")}
              >
                {mode === "signup" ? "Sign in" : "Create free account"}
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  );
}
